import { Injectable } from "@angular/core";
import {AccountService, AuthorizationService, SubscriptionService} from "../api/services";
import { Observable, of, forkJoin, BehaviorSubject } from "rxjs";
import { map, filter } from "rxjs/operators";
import { RootInjectorGuard } from "./root-injector-guard";
import { UserState } from "../api/models";
import { Router } from "@angular/router";
@Injectable({
  providedIn: "root",
})
export class GlobalApp extends RootInjectorGuard {
  private isFullScreenMode$ = new BehaviorSubject<boolean>(false);
  private showBranding$ = new BehaviorSubject<boolean>(false);
  private loggedIn$ = new BehaviorSubject<boolean>(false);
  private admin$ = new BehaviorSubject<boolean>(false);
  private supportAdmin$ = new BehaviorSubject<boolean>(false);
  private userName$ = new BehaviorSubject<string>("");
  private lastStatus: LastStatus = {
    lastInteractiveWasModified: undefined,
    lastInteractiveId: undefined,
    lastReportStandardTypeId: undefined,
    lastReportStandardLayoutId: undefined,
    lastReportEmbeddedTypeId: undefined,
    lastReportEmbeddedLayoutId: undefined,
    lastEditReportStandardTypeId: undefined,
    lastEditReportEmbeddedTypeId: undefined,
    lastPodLayoutInteractiveId: undefined,
    lastPodLayoutId: undefined
  };
  private userState$ = new BehaviorSubject<UserState>({
    invoiceManagerOnly: false,
    reportOnly: false,
    userManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    preFilteredInteractives: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false
  });

  public returnUrl = '';

  private loadingStatus$ = new BehaviorSubject<LoadingStatus>({
    isLoading: false,
    loadingMessage: "",
  });

  private loadingMessages: string[] = [];

  private isFullScreenMode: boolean = false;
  private showBranding: boolean = false;

  get isReportExport() {
    return this.isFullScreenMode$.asObservable();
  }

  get loadingStatus() {
    return this.loadingStatus$.asObservable();
  }

  get userState() {
    return this.userState$.asObservable();
  }

  get isLoggedIn() {
    return this.loggedIn$.asObservable();
  }

  get isAdmin() {
    return this.admin$.asObservable();
  }

  get isSupportAdmin() {
    return this.supportAdmin$.asObservable();
  }

  get currentUserName() {
    return this.userName$.asObservable();
  }

  get isShowingBranding() {
    return this.showBranding$.asObservable();
  }

  public logoutCheck: NodeJS.Timeout | undefined;

  constructor(
    private authService: AuthorizationService,
    private accountService: AccountService,
    private router: Router
  ) {
    super(GlobalApp);

    this.loggedIn$.next(this.isValidLogin());
    this.admin$.next(this.isValidAdmin());
    this.supportAdmin$.next(this.isValidSupportAdmin());
    this.userName$.next(localStorage.getItem("emailAddress"));
  }

  public timeoutCheck(app: GlobalApp) {
    if (!this.isFullScreenMode) {
      app.getAuthorizationStatus().subscribe((result) => {
        if (!result.isValid) {
          app.logOut();
          app.routeToLogin();
        } else {
          app.logoutCheck = setTimeout(app.timeoutCheck, 30000, app);
        }
      });
    }
  }

  private setupAuthCheck() {
    if (!this.isFullScreenMode) {
      if (!this.logoutCheck) {
        this.logoutCheck = setTimeout(this.timeoutCheck, 30000, this);
      }
    }
  }

  private cancelAuthCheck() {
    if (!this.isFullScreenMode) {
      if (this.logoutCheck) {
        clearTimeout(this.logoutCheck);
        this.logoutCheck = undefined;
      }
    }
  }

  loginNavigateBlock: string[] = [
    '/register',
    '/login',
    '/resetpassword',
    '/confirmemail'
  ]

  public routeToLogin() {
    let isValid = true;

    for (let i = 0; i < this.loginNavigateBlock.length; i++) {
      if (this.router.url.startsWith(this.loginNavigateBlock[i])) {
        isValid = false;
        break;
      }
    }

    if (isValid) {
      this.router.navigate(["login"]);
    }
  }

  public logIn(
    id: string,
    authToken: string,
    isAdmin: boolean,
    isSupportAdmin: boolean,
    emailAddress: string,
    lastInteractiveWasModified: boolean | undefined,
    lastInteractiveId: number | undefined,
    lastReportStandardTypeId: number | undefined,
    lastReportStandardLayoutId: number | undefined,
    lastReportEmbeddedTypeId: number | undefined,
    lastReportEmbeddedLayoutId: number | undefined,
    lastEditReportStandardTypeId: number | undefined,
    lastEditReportEmbeddedTypeId: number | undefined,
    lastPodLayoutInteractiveId: number | undefined,
    lastPodLayoutId: number | undefined
  ) {
    localStorage.setItem("currentAuthToken", authToken);
    localStorage.setItem("id", id);
    localStorage.setItem("isAdmin", isAdmin ? "t" : "f");
    localStorage.setItem("isSupportAdmin", isSupportAdmin ? "t" : "f");
    localStorage.setItem("emailAddress", emailAddress);

    this.loggedIn$.next(this.isValidLogin());
    this.admin$.next(this.isValidAdmin());
    this.supportAdmin$.next(this.isValidSupportAdmin());
    this.userName$.next(localStorage.getItem("emailAddress"));
    this.lastStatus = {
      lastInteractiveWasModified,
      lastInteractiveId,
      lastReportStandardTypeId,
      lastReportStandardLayoutId,
      lastReportEmbeddedTypeId,
      lastReportEmbeddedLayoutId,
      lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId,
      lastPodLayoutId
    };
    this.setupAuthCheck();
  }

  public logOut() {

    this.cancelAuthCheck();
    this.accountService.accountLogUserLogout().subscribe(result => {

    });

    localStorage.removeItem("currentAuthToken");
    localStorage.removeItem("id");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("isSupportAdmin");
    localStorage.removeItem("emailAddress");

    this.loggedIn$.next(this.isValidLogin());
    this.admin$.next(this.isValidAdmin());
    this.supportAdmin$.next(this.isValidSupportAdmin());
    this.userName$.next("");

    this.userState$.next({
      invoiceManagerOnly: false,
      reportOnly: false,
      userManagerOnly: false,
      interactives: [],
      modifiedInteractives: [],
      xmlas: [],
      reportExportState: {},
      isInteractiveUser: false,
      isReportExportUser: false,
      isEmbeddedUser: false
    });
  }

  public setLastInteractive(isModified: boolean, interactiveId: number) {
    this.lastStatus.lastInteractiveWasModified = isModified;
    this.lastStatus.lastInteractiveId = interactiveId;

    this.accountService.accountSetLast({
      lastInteractiveWasModified: this.lastStatus.lastInteractiveWasModified,
      lastInteractiveId: this.lastStatus.lastInteractiveId,
      lastReportStandardTypeId: this.lastStatus.lastReportStandardTypeId,
      lastReportStandardLayoutId: this.lastStatus.lastReportStandardLayoutId,
      lastReportEmbeddedTypeId: this.lastStatus.lastReportEmbeddedTypeId,
      lastReportEmbeddedLayoutId: this.lastStatus.lastReportEmbeddedLayoutId,
      lastEditReportStandardTypeId: this.lastStatus.lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId: this.lastStatus.lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId: this.lastStatus.lastPodLayoutInteractiveId,
      lastPodLayoutId: this.lastStatus.lastPodLayoutId
    }).subscribe((_) => { });
  }

  public setLastReport(reportStandardTypeId: number, reportStandardLayoutId: number | undefined) {
    this.lastStatus.lastReportStandardTypeId = reportStandardTypeId;
    this.lastStatus.lastReportStandardLayoutId = reportStandardLayoutId;

    this.accountService.accountSetLast({
      lastInteractiveWasModified: this.lastStatus.lastInteractiveWasModified,
      lastInteractiveId: this.lastStatus.lastInteractiveId,
      lastReportStandardTypeId: this.lastStatus.lastReportStandardTypeId,
      lastReportStandardLayoutId: this.lastStatus.lastReportStandardLayoutId,
      lastReportEmbeddedTypeId: this.lastStatus.lastReportEmbeddedTypeId,
      lastReportEmbeddedLayoutId: this.lastStatus.lastReportEmbeddedLayoutId,
      lastEditReportStandardTypeId: this.lastStatus.lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId: this.lastStatus.lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId: this.lastStatus.lastPodLayoutInteractiveId,
      lastPodLayoutId: this.lastStatus.lastPodLayoutId
    }).subscribe((_) => { });
  }
  public setLastWidget(reportEmbeddedTypeId: number, reportEmbeddedLayoutId: number | undefined) {
    this.lastStatus.lastReportEmbeddedTypeId = reportEmbeddedTypeId;
    this.lastStatus.lastReportEmbeddedLayoutId = reportEmbeddedLayoutId;

    this.accountService.accountSetLast({
      lastInteractiveWasModified: this.lastStatus.lastInteractiveWasModified,
      lastInteractiveId: this.lastStatus.lastInteractiveId,
      lastReportStandardTypeId: this.lastStatus.lastReportStandardTypeId,
      lastReportStandardLayoutId: this.lastStatus.lastReportStandardLayoutId,
      lastReportEmbeddedTypeId: this.lastStatus.lastReportEmbeddedTypeId,
      lastReportEmbeddedLayoutId: this.lastStatus.lastReportEmbeddedLayoutId,
      lastEditReportStandardTypeId: this.lastStatus.lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId: this.lastStatus.lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId: this.lastStatus.lastPodLayoutInteractiveId,
      lastPodLayoutId: this.lastStatus.lastPodLayoutId
    }).subscribe((_) => { });
  }

  public setLastEditStandardType(reportStandardTypeId: number) {
    this.lastStatus.lastEditReportStandardTypeId = reportStandardTypeId;

    this.accountService.accountSetLast({
      lastInteractiveWasModified: this.lastStatus.lastInteractiveWasModified,
      lastInteractiveId: this.lastStatus.lastInteractiveId,
      lastReportStandardTypeId: this.lastStatus.lastReportStandardTypeId,
      lastReportStandardLayoutId: this.lastStatus.lastReportStandardLayoutId,
      lastReportEmbeddedTypeId: this.lastStatus.lastReportEmbeddedTypeId,
      lastReportEmbeddedLayoutId: this.lastStatus.lastReportEmbeddedLayoutId,
      lastEditReportStandardTypeId: this.lastStatus.lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId: this.lastStatus.lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId: this.lastStatus.lastPodLayoutInteractiveId,
      lastPodLayoutId: this.lastStatus.lastPodLayoutId
    }).subscribe((_) => { });
  }

  public setLastEditReportEmbeddedType(lastReportEmbeddedTypeId: number) {
    this.lastStatus.lastEditReportEmbeddedTypeId = lastReportEmbeddedTypeId;

    this.accountService.accountSetLast({
      lastInteractiveWasModified: this.lastStatus.lastInteractiveWasModified,
      lastInteractiveId: this.lastStatus.lastInteractiveId,
      lastReportStandardTypeId: this.lastStatus.lastReportStandardTypeId,
      lastReportStandardLayoutId: this.lastStatus.lastReportStandardLayoutId,
      lastEditReportStandardTypeId: this.lastStatus.lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId: this.lastStatus.lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId: this.lastStatus.lastPodLayoutInteractiveId,
      lastPodLayoutId: this.lastStatus.lastPodLayoutId
    }).subscribe((_) => { });
  }

  public setLastPodLayoutInteractiveId(lastPodLayoutInteractiveId: number) {
    this.lastStatus.lastPodLayoutInteractiveId = lastPodLayoutInteractiveId;

    this.accountService.accountSetLast({
      lastInteractiveWasModified: this.lastStatus.lastInteractiveWasModified,
      lastInteractiveId: this.lastStatus.lastInteractiveId,
      lastReportStandardTypeId: this.lastStatus.lastReportStandardTypeId,
      lastReportStandardLayoutId: this.lastStatus.lastReportStandardLayoutId,
      lastEditReportStandardTypeId: this.lastStatus.lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId: this.lastStatus.lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId: this.lastStatus.lastPodLayoutInteractiveId,
      lastPodLayoutId: this.lastStatus.lastPodLayoutId
    }).subscribe((_) => { });
  }

  public setLastPodLayoutId(lastPodLayoutId: number) {
    this.lastStatus.lastPodLayoutId = lastPodLayoutId;

    this.accountService.accountSetLast({
      lastInteractiveWasModified: this.lastStatus.lastInteractiveWasModified,
      lastInteractiveId: this.lastStatus.lastInteractiveId,
      lastReportStandardTypeId: this.lastStatus.lastReportStandardTypeId,
      lastReportStandardLayoutId: this.lastStatus.lastReportStandardLayoutId,
      lastEditReportStandardTypeId: this.lastStatus.lastEditReportStandardTypeId,
      lastEditReportEmbeddedTypeId: this.lastStatus.lastEditReportEmbeddedTypeId,
      lastPodLayoutInteractiveId: this.lastStatus.lastPodLayoutInteractiveId,
      lastPodLayoutId: this.lastStatus.lastPodLayoutId
    }).subscribe((_) => { });
  }

  public getLastStatus() {
    this.setupAuthCheck();
    return this.lastStatus;
  }

  public getId(): string {
    this.setupAuthCheck();
    return localStorage.getItem("id");
  }

  public getAuthToken(): string {
    this.setupAuthCheck();
    return localStorage.getItem("currentAuthToken");
  }

  public isValidLogin(): boolean {
    this.setupAuthCheck();
    const authToken = localStorage.getItem("currentAuthToken");
    if (authToken !== null) return true;
    return false;
  }

  public isValidAdmin(): boolean {
    this.setupAuthCheck();
    const adminStatus = localStorage.getItem("isAdmin");

    return adminStatus !== null && adminStatus === "t";
  }

  public isValidSupportAdmin(): boolean {
    this.setupAuthCheck();
    const supportAdminStatus = localStorage.getItem("isSupportAdmin");

    return supportAdminStatus !== null && supportAdminStatus === "t";
  }

  public isExistingEmail(emailAddress: string): Observable<boolean> {
    this.setupAuthCheck();
    return this.authService.authorizationIsValidEmail(emailAddress).pipe();
  }

  public getAuthorizationStatus(): Observable<AuthorizationStatus> {
    this.setupAuthCheck();
    const status: AuthorizationStatus = {
      isValid: false,
      userId: "",
      authToken: "",
      isAdmin: false,
      isSupportAdmin: false
    };

    return forkJoin(
      this.authService.authorizationIsValidLogin(),
      this.authService.authorizationIsAdmin(),
      this.authService.authorizationIsSupportAdmin(),
      this.accountService.accountGetLastStatus()
    ).pipe(
      map((data) => {
        status.isValid = data[0] as boolean;
        if (status.isValid) {
          status.userId = localStorage.getItem("id");
          status.authToken = localStorage.getItem("currentAuthToken");
          status.isAdmin = data[1] as boolean;
          status.isSupportAdmin = data[2] as boolean;
        }

        this.lastStatus = {
          lastInteractiveWasModified: data[3].lastInteractiveWasModified,
          lastInteractiveId: data[3].lastInteractiveId,
          lastReportStandardTypeId: data[3].lastReportStandardTypeId,
          lastReportStandardLayoutId: data[3].lastReportStandardLayoutId,
          lastReportEmbeddedTypeId: data[3].lastReportEmbeddedTypeId,
          lastReportEmbeddedLayoutId: data[3].lastReportEmbeddedLayoutId,
          lastEditReportStandardTypeId: data[3].lastEditReportStandardTypeId,
          lastEditReportEmbeddedTypeId: data[3].lastEditReportEmbeddedTypeId,
          lastPodLayoutInteractiveId: data[3].lastPodLayoutInteractiveId,
          lastPodLayoutId: data[3].lastPodLayoutId
        }

        return status;
      })
    );
  }

  public setFullScreenMode() {
    this.cancelAuthCheck();
    this.isFullScreenMode = true;
    this.isFullScreenMode$.next(true);
  }

  public setShowBranding(willShowBranding: boolean) {
    this.showBranding = willShowBranding;
    this.showBranding$.next(willShowBranding);
  }

  public setUserState(userState: UserState) {
    this.setupAuthCheck();
    this.userState$.next(userState);
  }

  public refreshUserState(subscriptionService: SubscriptionService, callback: () => void | undefined = undefined) {
    this.setupAuthCheck();
    subscriptionService
      .subscriptionGetUserSubscriptionState()
      .subscribe((userState) => {
        this.setUserState(userState);
        if(callback) {
          callback();
        }
      });
  }

  public async refreshUserStateAsync(subscriptionService: SubscriptionService) : Promise<UserState> {
    this.setupAuthCheck();
    const newState = await subscriptionService.subscriptionGetUserSubscriptionState().toPromise();
    this.setUserState(newState);
    return newState;
  }

  public pushLoading(loadingMessage: string) {
    this.setupAuthCheck();
    this.loadingMessages.push(loadingMessage);
    this.loadingStatus$.next({ isLoading: true, loadingMessage });
  }

  public popLoading() {
    this.setupAuthCheck();
    if (this.loadingMessages.length > 0) {
      this.loadingMessages.splice(this.loadingMessages.length - 1, 1);
      if (this.loadingMessages.length > 0) {
        this.loadingStatus$.next({
          isLoading: true,
          loadingMessage: this.loadingMessages[this.loadingMessages.length - 1],
        });
      } else {
        this.loadingStatus$.next({ isLoading: false, loadingMessage: "" });
      }
    }
  }
}

export interface AuthorizationStatus {
  isValid: boolean;
  userId: string;
  authToken: string;
  isAdmin: boolean;
  isSupportAdmin: boolean;
}

export interface LoadingStatus {
  isLoading: boolean;
  loadingMessage: string;
}

export interface LastStatus {
  lastInteractiveWasModified: boolean | undefined,
  lastInteractiveId: number | undefined,
  lastReportStandardTypeId: number | undefined,
  lastReportStandardLayoutId: number | undefined,
  lastReportEmbeddedTypeId: number | undefined,
  lastReportEmbeddedLayoutId: number | undefined,
  lastEditReportStandardTypeId: number | undefined,
  lastEditReportEmbeddedTypeId: number | undefined,
  lastPodLayoutInteractiveId: number | undefined,
  lastPodLayoutId: number | undefined
}
